import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  Button,
  Toggle,
  Select,
  SelectItem,
  TextInput,
  Form,
  FormGroup,
  Loading,
  InlineNotification,
  Accordion,
  AccordionItem,
  Tag,
} from '@carbon/react';
import { Save, Reset } from '@carbon/react/icons';
import { useAuthStore } from '../../store/authStore';

interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  language: string;
  timezone: string;
  emailNotifications: {
    weeklyProgress: boolean;
    assignmentReminders: boolean;
    systemUpdates: boolean;
    marketingEmails: boolean;
  };
  privacySettings: {
    profileVisibility: 'public' | 'private' | 'friends';
    dataSharing: boolean;
    analyticsOptIn: boolean;
  };
  learningPreferences: {
    preferredPace: 'slow' | 'medium' | 'fast';
    difficultyLevel: 'beginner' | 'intermediate' | 'advanced';
    studyReminders: boolean;
    autoSave: boolean;
  };
  accessibility: {
    highContrast: boolean;
    largeText: boolean;
    reducedMotion: boolean;
    screenReader: boolean;
  };
}

const PreferencesPage: React.FC = () => {
  const { user } = useAuthStore();
  const [preferences, setPreferences] = useState<UserPreferences>({
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    emailNotifications: {
      weeklyProgress: true,
      assignmentReminders: true,
      systemUpdates: true,
      marketingEmails: false,
    },
    privacySettings: {
      profileVisibility: 'private',
      dataSharing: false,
      analyticsOptIn: true,
    },
    learningPreferences: {
      preferredPace: 'medium',
      difficultyLevel: 'intermediate',
      studyReminders: true,
      autoSave: true,
    },
    accessibility: {
      highContrast: false,
      largeText: false,
      reducedMotion: false,
      screenReader: false,
    },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // Simulate loading user preferences
    const loadPreferences = async () => {
      try {
        setLoading(true);
        // In production, this would call the preferences API
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Keep default preferences for now
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to load preferences' });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadPreferences();
    }
  }, [user]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      // In production, this would call the preferences update API
      await new Promise(resolve => setTimeout(resolve, 1500));

      setMessage({ type: 'success', text: 'Preferences saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save preferences. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPreferences({
      theme: 'light',
      language: 'en',
      timezone: 'UTC',
      emailNotifications: {
        weeklyProgress: true,
        assignmentReminders: true,
        systemUpdates: true,
        marketingEmails: false,
      },
      privacySettings: {
        profileVisibility: 'private',
        dataSharing: false,
        analyticsOptIn: true,
      },
      learningPreferences: {
        preferredPace: 'medium',
        difficultyLevel: 'intermediate',
        studyReminders: true,
        autoSave: true,
      },
      accessibility: {
        highContrast: false,
        largeText: false,
        reducedMotion: false,
        screenReader: false,
      },
    });
    setMessage({ type: 'success', text: 'Preferences reset to defaults' });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading preferences..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
      <Grid>
        <Column lg={16}>
          <div style={{ marginBottom: '2rem' }}>
            <h1
              style={{
                fontSize: '2.25rem',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--cds-interactive-01, #0f62fe)',
              }}
            >
              User Preferences
            </h1>
            <p style={{ fontSize: '1rem', color: 'var(--cds-text-secondary, #525252)' }}>
              Customize your learning experience and account settings
            </p>
            {user && (
              <Tag type="blue" style={{ marginTop: '1rem' }}>
                Logged in as: {user.email}
              </Tag>
            )}
          </div>

          {message && (
            <InlineNotification
              kind={message.type}
              title={message.type === 'success' ? 'Success' : 'Error'}
              subtitle={message.text}
              style={{ marginBottom: '2rem' }}
              onCloseButtonClick={() => setMessage(null)}
            />
          )}

          <Form
            onSubmit={e => {
              e.preventDefault();
              handleSave();
            }}
          >
            <Accordion>
              <AccordionItem title="General Settings">
                <Grid>
                  <Column lg={8}>
                    <FormGroup legendText="Appearance">
                      <Select
                        id="theme-select"
                        labelText="Theme"
                        value={preferences.theme}
                        onChange={e => setPreferences(prev => ({ ...prev, theme: e.target.value as any }))}
                      >
                        <SelectItem value="light" text="Light" />
                        <SelectItem value="dark" text="Dark" />
                        <SelectItem value="auto" text="Auto (System)" />
                      </Select>
                    </FormGroup>
                  </Column>
                  <Column lg={8}>
                    <FormGroup legendText="Localization">
                      <Select
                        id="language-select"
                        labelText="Language"
                        value={preferences.language}
                        onChange={e => setPreferences(prev => ({ ...prev, language: e.target.value }))}
                      >
                        <SelectItem value="en" text="English" />
                        <SelectItem value="es" text="Spanish" />
                        <SelectItem value="fr" text="French" />
                        <SelectItem value="de" text="German" />
                      </Select>
                    </FormGroup>
                  </Column>
                </Grid>
              </AccordionItem>

              <AccordionItem title="Email Notifications">
                <Grid>
                  <Column lg={8}>
                    <Toggle
                      id="weekly-progress"
                      labelText="Weekly Progress Reports"
                      toggled={preferences.emailNotifications.weeklyProgress}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          emailNotifications: { ...prev.emailNotifications, weeklyProgress: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="assignment-reminders"
                      labelText="Assignment Reminders"
                      toggled={preferences.emailNotifications.assignmentReminders}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          emailNotifications: { ...prev.emailNotifications, assignmentReminders: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="system-updates"
                      labelText="System Updates"
                      toggled={preferences.emailNotifications.systemUpdates}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          emailNotifications: { ...prev.emailNotifications, systemUpdates: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="marketing-emails"
                      labelText="Marketing Emails"
                      toggled={preferences.emailNotifications.marketingEmails}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          emailNotifications: { ...prev.emailNotifications, marketingEmails: checked },
                        }))
                      }
                    />
                  </Column>
                </Grid>
              </AccordionItem>

              <AccordionItem title="Privacy Settings">
                <Grid>
                  <Column lg={8}>
                    <Select
                      id="profile-visibility"
                      labelText="Profile Visibility"
                      value={preferences.privacySettings.profileVisibility}
                      onChange={e =>
                        setPreferences(prev => ({
                          ...prev,
                          privacySettings: { ...prev.privacySettings, profileVisibility: e.target.value as any },
                        }))
                      }
                    >
                      <SelectItem value="public" text="Public" />
                      <SelectItem value="private" text="Private" />
                      <SelectItem value="friends" text="Friends Only" />
                    </Select>
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="data-sharing"
                      labelText="Allow Data Sharing for Research"
                      toggled={preferences.privacySettings.dataSharing}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          privacySettings: { ...prev.privacySettings, dataSharing: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="analytics-opt-in"
                      labelText="Analytics and Usage Data"
                      toggled={preferences.privacySettings.analyticsOptIn}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          privacySettings: { ...prev.privacySettings, analyticsOptIn: checked },
                        }))
                      }
                    />
                  </Column>
                </Grid>
              </AccordionItem>

              <AccordionItem title="Learning Preferences">
                <Grid>
                  <Column lg={8}>
                    <Select
                      id="preferred-pace"
                      labelText="Preferred Learning Pace"
                      value={preferences.learningPreferences.preferredPace}
                      onChange={e =>
                        setPreferences(prev => ({
                          ...prev,
                          learningPreferences: { ...prev.learningPreferences, preferredPace: e.target.value as any },
                        }))
                      }
                    >
                      <SelectItem value="slow" text="Slow and Steady" />
                      <SelectItem value="medium" text="Moderate" />
                      <SelectItem value="fast" text="Fast-Paced" />
                    </Select>
                  </Column>
                  <Column lg={8}>
                    <Select
                      id="difficulty-level"
                      labelText="Default Difficulty Level"
                      value={preferences.learningPreferences.difficultyLevel}
                      onChange={e =>
                        setPreferences(prev => ({
                          ...prev,
                          learningPreferences: { ...prev.learningPreferences, difficultyLevel: e.target.value as any },
                        }))
                      }
                    >
                      <SelectItem value="beginner" text="Beginner" />
                      <SelectItem value="intermediate" text="Intermediate" />
                      <SelectItem value="advanced" text="Advanced" />
                    </Select>
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="study-reminders"
                      labelText="Study Reminders"
                      toggled={preferences.learningPreferences.studyReminders}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          learningPreferences: { ...prev.learningPreferences, studyReminders: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="auto-save"
                      labelText="Auto-save Progress"
                      toggled={preferences.learningPreferences.autoSave}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          learningPreferences: { ...prev.learningPreferences, autoSave: checked },
                        }))
                      }
                    />
                  </Column>
                </Grid>
              </AccordionItem>

              <AccordionItem title="Accessibility">
                <Grid>
                  <Column lg={8}>
                    <Toggle
                      id="high-contrast"
                      labelText="High Contrast Mode"
                      toggled={preferences.accessibility.highContrast}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          accessibility: { ...prev.accessibility, highContrast: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="large-text"
                      labelText="Large Text"
                      toggled={preferences.accessibility.largeText}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          accessibility: { ...prev.accessibility, largeText: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="reduced-motion"
                      labelText="Reduced Motion"
                      toggled={preferences.accessibility.reducedMotion}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          accessibility: { ...prev.accessibility, reducedMotion: checked },
                        }))
                      }
                    />
                  </Column>
                  <Column lg={8}>
                    <Toggle
                      id="screen-reader"
                      labelText="Screen Reader Optimizations"
                      toggled={preferences.accessibility.screenReader}
                      onToggle={checked =>
                        setPreferences(prev => ({
                          ...prev,
                          accessibility: { ...prev.accessibility, screenReader: checked },
                        }))
                      }
                    />
                  </Column>
                </Grid>
              </AccordionItem>
            </Accordion>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '2rem',
                paddingTop: '2rem',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Button kind="primary" type="submit" disabled={saving} renderIcon={Save}>
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
              <Button kind="secondary" onClick={handleReset} renderIcon={Reset}>
                Reset to Defaults
              </Button>
            </div>
          </Form>
        </Column>
      </Grid>
    </div>
  );
};

export default PreferencesPage;
